export const USAGETAP_PROFILER_SCHEMA_VERSION =
  "usagetap.profiler.call-input.v1" as const;

type Message = {
  role?: unknown;
  content?: unknown;
};

export type UsageTapExportCall = {
  id?: string | null;
  projectId?: string | null;
  status?: string | null;
  customerId?: string | null;
  serviceId?: string | null;
  modelId?: string | null;
  messages?: string | null;
  prompt?: string | null;
  response?: string | null;
  tokenCountPrompt?: number | null;
  tokenCountResponse?: number | null;
  createdAt?: string | null;
  quality?: number | null;
  agentId?: string | null;
  conversationId?: string | null;
  batch?: boolean | null;
  cacheReadTokens?: number | null;
  cacheWriteTokens?: number | null;
  cacheStatus?: string | null;
  compactionTriggered?: boolean | null;
  compactionTokensSaved?: number | null;
  curatedTokensSaved?: number | null;
  curatedItems?: number | null;
  service?: {
    name?: string | null;
    vendor?: string | null;
    group?: { systemInstructions?: string | null } | null;
  } | null;
  model?: {
    name?: string | null;
    vendor?: string | null;
    modelKey?: string | null;
    modelKeyLatest?: string | null;
  } | null;
};

export type ResolvedCallContent = {
  messages?: string;
  prompt?: string;
  response?: string;
};

type Dataset = {
  id?: string;
  name?: string;
  source?: string;
  metadata?: Record<string, unknown>;
};

export type UsageTapProfilerCallInput = {
  schema_version: typeof USAGETAP_PROFILER_SCHEMA_VERSION;
  dataset?: Dataset;
  calls: Array<{
    id: string;
    system_instruction: string;
    prompt: string;
    response: {
      content: string;
      finish_reason: string | null;
      metadata: Record<string, unknown>;
    };
    provider?: string;
    model?: string;
    created_at?: string;
    metadata: Record<string, unknown>;
  }>;
};

const isPresent = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const compactObject = (entries: Array<[string, unknown]>) =>
  Object.fromEntries(
    entries.filter(([, value]) => value !== undefined && value !== null)
  );

const stringifyContent = (content: unknown): string => {
  if (typeof content === "string") return content;

  if (Array.isArray(content)) {
    return content.map(stringifyContent).filter(isPresent).join("\n");
  }

  if (content && typeof content === "object") {
    const part = content as Record<string, unknown>;
    for (const key of ["text", "input_text", "output_text", "content"]) {
      if (key in part) {
        const value = stringifyContent(part[key]);
        if (isPresent(value)) return value;
      }
    }

    return JSON.stringify(content);
  }

  return content == null ? "" : String(content);
};

const parseMessages = (messages: string | undefined): Message[] => {
  if (!isPresent(messages)) return [];

  try {
    const parsed = JSON.parse(messages);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.messages)) return parsed.messages;
  } catch {
    // A malformed messages field should not prevent exporting the call's
    // separately stored prompt and response.
  }

  return [];
};

const getProvider = (call: UsageTapExportCall): string | undefined => {
  const serviceVendor = call.service?.vendor;
  if (isPresent(serviceVendor)) return serviceVendor;

  const modelVendor = call.model?.vendor;
  if (isPresent(modelVendor)) return modelVendor;

  if (isPresent(call.modelId) && call.modelId.includes("/")) {
    return call.modelId.split("/")[0];
  }

  return undefined;
};

const getModel = (call: UsageTapExportCall): string | undefined => {
  for (const candidate of [
    call.model?.modelKeyLatest,
    call.model?.modelKey,
    call.modelId,
    call.model?.name,
    call.service?.name,
  ]) {
    if (isPresent(candidate)) return candidate;
  }

  return undefined;
};

const getCreatedAt = (createdAt: string | null | undefined) => {
  if (!isPresent(createdAt)) return undefined;
  const timestamp = Date.parse(createdAt);
  return Number.isNaN(timestamp) ? undefined : new Date(timestamp).toISOString();
};

export const isS3CallContentReference = (content: unknown): content is string =>
  typeof content === "string" &&
  (content.startsWith("s3://") || content.startsWith("S3:s3://"));

export const buildUsageTapProfilerCallInput = (
  call: UsageTapExportCall,
  resolvedContent: ResolvedCallContent = {},
  dataset?: Dataset,
  projectSystemInstruction?: string | null
): UsageTapProfilerCallInput => {
  if (!isPresent(call.id)) {
    throw new Error("The selected call does not have an ID.");
  }

  const messagesText = resolvedContent.messages ?? call.messages ?? undefined;
  const messages = parseMessages(messagesText);
  const systemMessages = messages
    .filter((message) => message?.role === "system")
    .map((message) => stringifyContent(message.content).trim())
    .filter(isPresent);

  const systemInstruction =
    systemMessages.join("\n\n") ||
    call.service?.group?.systemInstructions?.trim() ||
    projectSystemInstruction?.trim() ||
    "";

  const promptFromCall = (resolvedContent.prompt ?? call.prompt ?? "").trim();
  const promptFromMessages = [...messages]
    .reverse()
    .find((message) => message?.role === "user");
  const prompt =
    promptFromCall || stringifyContent(promptFromMessages?.content).trim();

  if (!prompt) {
    throw new Error("The selected call does not contain a prompt to export.");
  }

  const response = resolvedContent.response ?? call.response ?? "";
  const provider = getProvider(call);
  const model = getModel(call);
  const createdAt = getCreatedAt(call.createdAt);

  const callMetadata = compactObject([
    ["project_id", call.projectId],
    ["customer_id", call.customerId],
    ["service_id", call.serviceId],
    ["status", call.status],
    ["prompt_tokens", call.tokenCountPrompt],
    ["response_tokens", call.tokenCountResponse],
    ["quality", call.quality],
    ["agent_id", call.agentId],
    ["conversation_id", call.conversationId],
    ["batch", call.batch],
    ["cache_read_tokens", call.cacheReadTokens],
    ["cache_write_tokens", call.cacheWriteTokens],
    ["cache_status", call.cacheStatus],
    ["compaction_triggered", call.compactionTriggered],
    ["compaction_tokens_saved", call.compactionTokensSaved],
    ["curated_tokens_saved", call.curatedTokensSaved],
    ["curated_items", call.curatedItems],
  ]);

  const profilerCall = {
    id: call.id,
    system_instruction: systemInstruction,
    prompt,
    response: {
      content: response,
      finish_reason: null,
      metadata: compactObject([
        ["token_count", call.tokenCountResponse],
        ["status", call.status],
      ]),
    },
    ...(provider ? { provider } : {}),
    ...(model ? { model } : {}),
    ...(createdAt ? { created_at: createdAt } : {}),
    metadata: callMetadata,
  };

  return {
    schema_version: USAGETAP_PROFILER_SCHEMA_VERSION,
    ...(dataset ? { dataset } : {}),
    calls: [profilerCall],
  };
};

export const downloadUsageTapProfilerCallInput = (
  input: UsageTapProfilerCallInput
) => {
  const callId = input.calls[0]?.id || "call";
  const safeCallId = callId.replace(/[^a-zA-Z0-9._-]+/g, "-");
  const blob = new Blob([JSON.stringify(input, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `usagetap-profiler-call-${safeCallId}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
